"use client"

import { useEffect, useState, type CSSProperties } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { HugeiconsIcon } from "@hugeicons/react"
import { Alien02Icon, LayoutDashboardIcon } from "@hugeicons/core-free-icons"
import { ChevronLeft, ChevronRight, Folder, Gamepad2, Mouse, Palette, Visor } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { FeatureFlagWrapper } from "@/components/feature-flag-wrapper"
import { FitTitle } from "@/components/fit-title"
import { useSettingsStore } from "@/lib/settings-store"
import { useSourceStore, type SourceConfig } from "@/lib/source-store"
import { useTagSetStore } from "@/lib/tag-set-store"
import { useAuthStore } from "@/lib/auth-store"
import { useRecentBrowseStore } from "@/lib/recent-browse-store"
import type { BooruSite } from "@/lib/booru-api"

// "Start browsing" turns on this one source (falls back to the first enabled one if it isn't available).
const START_SOURCE_ID = "safebooru"
// Source tiles per row by screen width: 2 under 360px, 3 from 360px, 4 from 480px. Still one row until 640px; from 640px up
// and with more sources than fit in one row, the picker uses two rows per page (so 8 per page).
const BREAKPOINT_PHONE = 360
const BREAKPOINT_FOUR_ACROSS = 480
const BREAKPOINT_TWO_ROWS = 640
const RECENTS_PER_PAGE = 2

// Look-and-feel values come straight from the approved prototype (exact colours, shadows and sizes), so the real
// panel matches it. The few `dark:` fallbacks keep it readable if the app is in dark mode.
const SHADOW = "shadow-[0_1px_2px_rgba(30,20,60,0.10),0_4px_14px_rgba(30,20,60,0.06)]"
const SHADOW_LG_HOVER = "hover:shadow-[0_8px_28px_rgba(30,20,60,0.16)]"
const EYEBROW = "text-[10px] font-bold uppercase tracking-[0.08em] sm:text-[11px] text-[#6d4aff] dark:text-primary"
const MUTED = "text-[#6b6779] dark:text-muted-foreground"

// Each source tile is tinted to match its own icon (rgba values from the prototype).
const SOURCE_TINTS: Record<string, { a: string; b: string; line: string }> = {
  sakugabooru: { a: "rgba(239,68,68,.22)", b: "rgba(244,63,94,.09)", line: "rgba(239,68,68,.45)" },
  safebooru: { a: "rgba(20,184,166,.22)", b: "rgba(34,197,94,.10)", line: "rgba(20,184,166,.45)" },
  danbooru: { a: "rgba(217,119,6,.24)", b: "rgba(234,179,8,.10)", line: "rgba(217,119,6,.45)" },
  gelbooru: { a: "rgba(37,99,235,.22)", b: "rgba(14,165,233,.10)", line: "rgba(37,99,235,.45)" },
  yandere: { a: "rgba(236,72,153,.20)", b: "rgba(244,114,182,.08)", line: "rgba(236,72,153,.42)" },
  erome: { a: "rgba(244,63,94,.20)", b: "rgba(251,113,133,.08)", line: "rgba(244,63,94,.42)" },
}
const DEFAULT_TINT = { a: "rgba(109,74,255,.10)", b: "rgba(109,74,255,.04)", line: "#d9d6e2" }

const tintStyle = (id: string): CSSProperties => {
  const t = SOURCE_TINTS[id] ?? DEFAULT_TINT
  return { borderColor: t.line, backgroundImage: `linear-gradient(135deg, ${t.a}, ${t.b})` }
}

// Same hues as the header buttons: a soft tint at rest, the strong one on hover (exact prototype values).
const AREAS = [
  { key: "collections", href: "/?view=collections", hue: "#3b82f6" },
  { key: "editors", href: "/?view=editors", hue: "#ec4899" },
  { key: "games", href: "/?view=game-arena", hue: "#22c55e" },
  { key: "gooncave", href: "/?view=gooncave", hue: "#a855f7" },
  { key: "vr", href: "/vr", hue: "#eab308" },
  { key: "goonpals", href: "/goonpals", hue: "#06b6d4", flag: "goonpals" },
  { key: "commissions", href: "/commissions", hue: "#f97316" },
] as const

type AreaKey = (typeof AREAS)[number]["key"]

function AreaIcon({ name }: { name: AreaKey }) {
  const cls = "h-[22px] w-[22px]"
  switch (name) {
    case "collections":
      return <Folder className={cls} />
    case "editors":
      return <Mouse className={cls} />
    case "games":
      return <Gamepad2 className={cls} />
    case "gooncave":
      return <HugeiconsIcon icon={LayoutDashboardIcon} strokeWidth={2} className={cls} />
    case "vr":
      return <Visor className={cls} />
    case "goonpals":
      return <HugeiconsIcon icon={Alien02Icon} strokeWidth={2} className={cls} />
    case "commissions":
      return <Palette className={cls} />
  }
}

function chunk<T>(items: T[], size: number): T[][] {
  const pages: T[][] = []
  for (let i = 0; i < items.length; i += size) pages.push(items.slice(i, i + size))
  return pages
}

// With lots of pages the dots would run off the screen, so only a window of MAX_DOTS shows and the dots shrink the
// further they are from the current page (the way photo galleries do it).
const MAX_DOTS = 7

function dotSizes(page: number, pages: number): { index: number; size: number }[] {
  if (pages <= MAX_DOTS) return Array.from({ length: pages }, (_, index) => ({ index, size: 7 }))
  const start = Math.max(0, Math.min(pages - MAX_DOTS, page - Math.floor(MAX_DOTS / 2)))
  return Array.from({ length: MAX_DOTS }, (_, i) => {
    const index = start + i
    const distance = Math.abs(index - page)
    const cutOff = (i === 0 && start > 0) || (i === MAX_DOTS - 1 && start + MAX_DOTS < pages)
    const size = distance === 0 ? 7 : cutOff ? 3 : distance === 1 ? 6 : distance === 2 ? 5 : 4
    return { index, size }
  })
}

function Pager({ page, pages, onPage, label }: { page: number; pages: number; onPage: (p: number) => void; label: string }) {
  const t = useTranslations("welcomePicker")
  if (pages <= 1) return null
  const nav = cn(
    "grid h-7 w-7 place-items-center rounded-full border border-[#d9d6e2] bg-[#f7f6fa] text-[#1c1a24] disabled:cursor-default disabled:opacity-35 dark:border-border dark:bg-card dark:text-foreground",
    SHADOW,
  )
  return (
    <div className="flex items-center gap-2" role="group" aria-label={label}>
      <button type="button" className={nav} disabled={page === 0} onClick={() => onPage(page - 1)} aria-label={t("previous")}>
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-center gap-[5px]" aria-hidden="true">
        {dotSizes(page, pages).map(({ index, size }) => (
          <span
            key={index}
            style={{ width: size, height: size }}
            className={cn("rounded-full transition-all duration-200", index === page ? "bg-[#6d4aff]" : "bg-[#d9d6e2] dark:bg-border")}
          />
        ))}
      </div>
      <button type="button" className={nav} disabled={page === pages - 1} onClick={() => onPage(page + 1)} aria-label={t("next")}>
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function SourceIcon({ source, size }: { source: SourceConfig | undefined; size: number }) {
  if (source?.faviconUrl) {
    // Favicons come from third-party hosts; a plain <img> keeps them out of the image optimiser.
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={source.faviconUrl} alt="" width={size} height={size} referrerPolicy="no-referrer" className="rounded-sm" />
  }
  return <span className="leading-none" style={{ fontSize: size * 0.75 }}>{source?.icon || "🌐"}</span>
}

// The Goonru "G" on the purple gradient tile, same as the prototype.
function Mascot({ size, radius, className }: { size: number; radius: number; className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn("shrink-0", className)}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        backgroundImage: "url(/favicon.png), linear-gradient(135deg, #8b6bff, #5b3bf0)",
        backgroundSize: "70%, cover",
        backgroundPosition: "center, center",
        backgroundRepeat: "no-repeat, no-repeat",
        boxShadow: "0 10px 28px rgba(109,74,255,.45)",
      }}
    />
  )
}

// Greets by the first word of the display name (or the username), capped so the header never breaks.
const MAX_GREETING_NAME = 20 // the longest username a new account can have

function greetingName(profile: { display_name?: string | null; username?: string | null } | null | undefined): string {
  const source = (profile?.display_name || profile?.username || "").trim()
  const first = source.split(/\s+/)[0] ?? ""
  const chars = Array.from(first)
  return chars.length > MAX_GREETING_NAME ? chars.slice(0, MAX_GREETING_NAME).join("") + "…" : first
}

function timeAgo(at: number, locale?: string): string {
  const diffMin = Math.round((at - Date.now()) / 60000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, "minute")
  const hours = Math.round(diffMin / 60)
  if (Math.abs(hours) < 24) return rtf.format(hours, "hour")
  return rtf.format(Math.round(hours / 24), "day")
}

const PRIMARY_BTN =
  "inline-flex items-center justify-center gap-2 rounded-xl bg-[#6d4aff] font-semibold text-white shadow-[0_6px_18px_rgba(109,74,255,0.35)] transition hover:brightness-110 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-[#6d4aff]"

/** Shown when no source is selected yet. First visit: a welcome with one-tap Start browsing (Sakugabooru only),
 * the visitor's enabled sources (paged) and the rest of Goonru. Return visit (they have recent searches): the same,
 * with "Welcome back", a Continue button and their recents on top. On short windows the big centered header
 * collapses to a slim one-row header so everything still fits. */
export function WelcomeSourcePicker() {
  const t = useTranslations("welcomePicker")
  const router = useRouter()
  const sources = useSourceStore((s) => s.sources)
  const setSelectedSites = useSettingsStore((s) => s.setSelectedSites)
  const activeSetId = useTagSetStore((s) => s.activeSetId)
  const updateSet = useTagSetStore((s) => s.updateSet)
  const recents = useRecentBrowseStore((s) => s.recents)

  // Persisted stores only exist in the browser; wait for mount so the server and first client render agree.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const [sourcePage, setSourcePage] = useState(0)
  const [columns, setColumns] = useState(4)
  const [twoRows, setTwoRows] = useState(true)
  useEffect(() => {
    const queries = [BREAKPOINT_PHONE, BREAKPOINT_FOUR_ACROSS, BREAKPOINT_TWO_ROWS].map((w) => window.matchMedia(`(min-width: ${w}px)`))
    const [phone, four, rows] = queries
    const update = () => {
      setColumns(four.matches ? 4 : phone.matches ? 3 : 2)
      setTwoRows(rows.matches)
      setSourcePage(0)
    }
    update()
    queries.forEach((q) => q.addEventListener("change", update))
    return () => queries.forEach((q) => q.removeEventListener("change", update))
  }, [])
  const [recentPage, setRecentPage] = useState(0)

  // Out-of-service sources (e.g. Sakugabooru) are left out even for visitors who switched them on in an earlier visit.
  const enabled = sources.filter((s) => s.enabled && !s.disabled)
  // Phones get a single row (it all fits one screen); 640px and up use two rows once there are more sources than columns.
  const rows = twoRows && enabled.length > columns ? 2 : 1
  const sourcesPerPage = columns * rows
  const sourcePages = chunk(enabled, sourcesPerPage)
  const sourceById = new Map(sources.map((s) => [s.id, s]))
  const usableRecents = mounted ? recents.filter((r) => sourceById.get(r.site)?.enabled && !sourceById.get(r.site)?.disabled) : []
  const recentPages = chunk(usableRecents, RECENTS_PER_PAGE)
  const isReturn = usableRecents.length > 0

  const pickSource = (id: string) => setSelectedSites([id as BooruSite])

  const startBrowsing = () => {
    const start = enabled.find((s) => s.id === START_SOURCE_ID) ?? enabled[0]
    if (start) pickSource(start.id)
  }

  const openRecent = (site: string, tags: string[]) => {
    pickSource(site)
    if (activeSetId) updateSet(activeSetId, tags)
  }

  const continueBrowsing = () => {
    const latest = usableRecents[0]
    if (latest) openRecent(latest.site, latest.tags)
    else startBrowsing()
  }

  // Returning visitors get a greeting that changes from visit to visit (picked once per browser session).
  const profile = useAuthStore((st) => st.profile)
  const greetings = t.raw("greetings") as string[]
  const [greetingIndex, setGreetingIndex] = useState(0)
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("welcome-greeting")
      const index = saved !== null ? Number(saved) : Math.floor(Math.random() * greetings.length)
      if (saved === null) sessionStorage.setItem("welcome-greeting", String(index))
      setGreetingIndex(Number.isFinite(index) ? index % greetings.length : 0)
    } catch {
      // sessionStorage can be blocked; the first greeting is fine
    }
  }, [greetings.length])
  const greeting = greetings[greetingIndex] ?? greetings[0]
  const name = greetingName(profile)
  // Best to worst: the picked greeting with the name, then the two shortest greetings with the name (these rescue long
  // names), and finally the picked greeting alone, which always fits.
  const byLength = [...greetings].sort((a, b) => a.length - b.length)
  const returnCandidates = Array.from(
    new Set(
      name
        ? [
            t("greetingNamed", { greeting, name }),
            t("greetingNamed", { greeting: byLength[0], name }),
            t("greetingNamed", { greeting: byLength[1] ?? byLength[0], name }),
            t("greetingPlain", { greeting }),
          ]
        : [t("greetingPlain", { greeting })],
    ),
  )
  const heading = isReturn ? returnCandidates[0] : t("welcome")
  const titleCandidates = isReturn ? returnCandidates : [t("welcome")]
  const tagline = isReturn ? t("welcomeBackTagline") : t("tagline")
  const action = isReturn ? t("continue") : t("start")
  const onAction = isReturn ? continueBrowsing : startBrowsing

  return (
    <div className="flex min-h-[400px] items-center justify-center p-4">
      <section
        aria-labelledby="welcome-title"
        className="w-full max-w-[900px] rounded-[20px] border border-[#d9d6e2] bg-[rgba(247,246,250,0.86)] p-4 text-[#1c1a24] shadow-[0_8px_28px_rgba(30,20,60,0.16)] backdrop-blur-[10px] dark:border-border dark:bg-card/85 dark:text-foreground sm:p-[22px]"
      >
        {/* Roomy header (first visit, normal windows): centered, big mascot, big button. A returning visitor always gets the
            slim one below, so the recents get the room. */}
        <div className={cn("hidden flex-col items-center pt-1.5 text-center", !isReturn && "[@media(min-height:600px)]:flex")}>
          <Mascot size={72} radius={20} />
          <h2 id="welcome-title" className="mt-2.5 text-[28px] font-[750] leading-tight">{heading}</h2>
          <p className={cn("mt-1 text-[13.5px]", MUTED)}>{tagline}</p>
          <button type="button" className={cn(PRIMARY_BTN, "mt-3.5 px-7 py-3 text-[15px]")} onClick={onAction}>
            {action} <span aria-hidden="true">→</span>
          </button>
        </div>

        {/* Slim header (return visits, and short windows): one row, button beside the words; stacks on narrow screens. */}
        <div className={cn("flex flex-wrap items-center gap-3.5", !isReturn && "[@media(min-height:600px)]:hidden")}>
          <Mascot size={52} radius={15} className="[box-shadow:0_6px_16px_rgba(109,74,255,.4)!important]" />
          <div className="min-w-0 flex-1 basis-48">
            <FitTitle candidates={titleCandidates} />
            <p className={cn("mt-0.5 text-[12.5px] sm:text-[13.5px]", MUTED)}>{tagline}</p>
          </div>
          <button type="button" className={cn(PRIMARY_BTN, "w-full px-[18px] py-2.5 text-sm sm:w-auto")} onClick={onAction}>
            {action} <span aria-hidden="true">→</span>
          </button>
        </div>

        {isReturn && (
          <div className="mt-4 border-t border-[#d9d6e2] pt-3 dark:border-border">
            <div className="mb-2.5 flex items-center justify-between gap-2.5">
              <h3 className={EYEBROW}>{t("recent")}</h3>
              <Pager page={recentPage} pages={recentPages.length} onPage={setRecentPage} label={t("recent")} />
            </div>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {(recentPages[recentPage] ?? []).map((r) => {
                const source = sourceById.get(r.site)
                return (
                  <button
                    key={`${r.site}:${r.tags.join(",")}`}
                    type="button"
                    onClick={() => openRecent(r.site, r.tags)}
                    style={tintStyle(r.site)}
                    className={cn("flex min-w-0 items-center gap-2.5 rounded-[14px] border px-3 py-2.5 text-left transition hover:-translate-y-0.5", SHADOW, SHADOW_LG_HOVER)}
                  >
                    <span className="grid h-[34px] w-[34px] shrink-0 place-items-center overflow-hidden rounded-[10px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.18)]">
                      <SourceIcon source={source} size={22} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-bold">{r.tags.join(" ")}</span>
                      <span className={cn("block truncate text-[11.5px]", MUTED)}>
                        {source?.name ?? r.site} · {timeAgo(r.at)}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <div className="mt-4 border-t border-[#d9d6e2] pt-3 dark:border-border">
          <div className="mb-2.5 flex items-center justify-between gap-2.5">
            <h3 className={cn(EYEBROW, "min-w-0")}>{isReturn ? t("sources") : t("pickSource")}</h3>
            <Pager page={sourcePage} pages={sourcePages.length} onPage={setSourcePage} label={t("sources")} />
          </div>
          <div className="grid grid-cols-2 gap-2.5 min-[360px]:grid-cols-3 min-[480px]:grid-cols-4">
            {(sourcePages[sourcePage] ?? []).map((source) => (
              <button
                key={source.id}
                type="button"
                onClick={() => pickSource(source.id)}
                style={tintStyle(source.id)}
                className={cn("relative flex min-w-0 flex-col items-center justify-center gap-2 rounded-[14px] border px-1.5 pb-[11px] pt-3.5 text-center transition hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-[#6d4aff]", SHADOW, SHADOW_LG_HOVER)}
              >
                <span className="grid h-[50px] w-[50px] place-items-center overflow-hidden rounded-[15px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.18)]">
                  <SourceIcon source={source} size={32} />
                </span>
                <span className="block w-full truncate text-[12.5px] font-bold">{source.name}</span>
              </button>
            ))}
          </div>
          <div className="mt-2 text-right">
            <Link href="/sources" className="text-xs font-semibold text-[#6d4aff] hover:underline dark:text-primary sm:text-[13px]">
              {t("manage")}
            </Link>
          </div>
        </div>

        <div className="mt-3 border-t border-[#d9d6e2] pt-3 dark:border-border">
          <h3 className={cn("mb-2.5", EYEBROW)}>{t("alsoOn")}</h3>
          <div className="flex flex-wrap justify-center gap-2.5">
            {AREAS.map((area) => {
              const card = (
                <button
                  key={area.key}
                  type="button"
                  onClick={() => router.push(area.href)}
                  style={{ "--h": area.hue } as CSSProperties}
                  className={cn(
                    "group flex min-w-0 items-center gap-2.5 rounded-xl border border-[#d9d6e2] bg-[#f7f6fa] p-2.5 text-left transition hover:border-[#6d4aff] dark:border-border dark:bg-card w-full min-[360px]:w-[calc(50%-5px)] sm:w-[calc(33.333%-7px)] lg:w-[calc(25%-8px)]",
                    SHADOW,
                  )}
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[color-mix(in_srgb,var(--h)_24%,transparent)] text-[color-mix(in_srgb,var(--h)_78%,#000)] transition-colors group-hover:bg-[color-mix(in_srgb,var(--h)_62%,transparent)] group-hover:text-white">
                    <AreaIcon name={area.key} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-bold">{t(`area.${area.key}`)}</span>
                    <span className={cn("block text-xs max-sm:line-clamp-2 sm:truncate", MUTED)}>{t(`areaDesc.${area.key}`)}</span>
                  </span>
                </button>
              )
              return "flag" in area ? (
                <FeatureFlagWrapper key={area.key} featureName={area.flag}>
                  {card}
                </FeatureFlagWrapper>
              ) : (
                card
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}
